import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useEventsApi, useReceiptsApi } from 'services/api';
import { AttendeeCheckIn } from './AttendeeCheckIn';
import { CHECK_IN_COLORS } from './colors';
import { OrganizerCheckIn } from './OrganizerCheckIn';
import { CheckInMode, EventCheckInSheetProps, TicketHolder } from './types';

export const EventCheckInSheet: React.FC<EventCheckInSheetProps> = ({
  open,
  onClose,
  event,
  receiptId: receiptIdProp,
  tickets: ticketsProp,
  forceOrganizer = false,
}) => {
  const eventsApi = useEventsApi();
  const receiptsApi = useReceiptsApi();
  const [mode, setMode] = useState<CheckInMode | null>(null);
  const [loading, setLoading] = useState(true);
  const [receiptId, setReceiptId] = useState(receiptIdProp || '');
  const [tickets, setTickets] = useState<TicketHolder[]>(ticketsProp || []);
  const ticketsPropRef = useRef(ticketsProp);
  ticketsPropRef.current = ticketsProp;

  useEffect(() => {
    if (!open) {
      setMode(null);
      setLoading(true);
      return;
    }

    let cancelled = false;

    const boot = async () => {
      setLoading(true);

      const nextReceiptId = receiptIdProp || '';
      let nextTickets: TicketHolder[] = ticketsPropRef.current
        ? ticketsPropRef.current.map(t => ({ ...t }))
        : [];

      if (forceOrganizer) {
        if (!cancelled) {
          setReceiptId(nextReceiptId);
          setTickets(nextTickets);
          setMode('organizer');
          setLoading(false);
        }
        return;
      }

      const access = await eventsApi.getCheckInAccess(event.id);
      const canOrganize = access.success && !!access.value.canOrganize;

      if (nextReceiptId) {
        const receipt = await receiptsApi.getReceipt(nextReceiptId);
        if (receipt.success) {
          nextTickets = (receipt.value.tickets || []).map(t => ({
            firstName: t.firstName,
            lastName: t.lastName,
            email: t.email,
            ticketNumber: t.ticketNumber,
            typeId: t.typeId,
            checkedInDate: t.checkInDate,
          }));
        }
      }

      if (!cancelled) {
        setReceiptId(nextReceiptId);
        setTickets(nextTickets);
        setMode(canOrganize ? 'organizer' : 'attendee');
        setLoading(false);
      }
    };

    void boot();

    return () => {
      cancelled = true;
    };
  }, [open, event?.id, forceOrganizer, receiptIdProp, eventsApi, receiptsApi]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      sx={{
        '& .MuiDialog-paper': {
          margin: { xs: 0, md: 'auto' },
          maxWidth: { xs: '100%', md: 430 },
          width: { xs: '100%', md: 430 },
          height: { xs: '100%', md: 'min(844px, 96vh)' },
          maxHeight: { xs: '100%', md: 'min(844px, 96vh)' },
          borderRadius: { xs: 0, md: '12px' },
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: CHECK_IN_COLORS.white,
        },
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(20, 31, 51, 0.55)',
        },
      }}
    >
      <Box
        sx={{
          height: '56px',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          flexShrink: 0,
          backgroundColor: CHECK_IN_COLORS.white,
        }}
      >
        <IconButton
          onClick={onClose}
          aria-label="Close"
          sx={{
            position: 'absolute',
            left: '16px',
            top: '8px',
            color: CHECK_IN_COLORS.text,
          }}
        >
          <CloseIcon sx={{ fontSize: 24 }} />
        </IconButton>
        <Typography
          sx={{
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 600,
            fontSize: '16px',
            lineHeight: '24px',
            color: CHECK_IN_COLORS.text,
          }}
        >
          Event Check-In
        </Typography>
      </Box>

      {loading || !mode ? (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: CHECK_IN_COLORS.sheetBg,
          }}
        >
          <CircularProgress sx={{ color: CHECK_IN_COLORS.navy }} />
        </Box>
      ) : mode === 'organizer' ? (
        <OrganizerCheckIn event={event} onClose={onClose} />
      ) : (
        <AttendeeCheckIn
          event={event}
          receiptId={receiptId}
          tickets={tickets}
          onClose={onClose}
        />
      )}
    </Dialog>
  );
};
